import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ActionPopupProps {
  isOpen: boolean;
  icon: React.ReactNode;
  iconTone?: "brand" | "danger";
  title: string;
  description: React.ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  confirmTone?: "brand" | "danger";
  loading?: boolean;
  loadingLabel?: string;
  disableBackdropClose?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

const ActionPopup: React.FC<ActionPopupProps> = ({
  isOpen,
  icon,
  iconTone = "brand",
  title,
  description,
  cancelLabel,
  confirmLabel,
  confirmTone = "brand",
  loading = false,
  loadingLabel,
  disableBackdropClose = false,
  onCancel,
  onConfirm
}) => {
  if (!isOpen) return null;

  const backdropClosable = !loading && !disableBackdropClose;

  return (
    <>
      <div
        className="fixed inset-0 z-[60] bg-[#0a0d12]/65"
        onClick={backdropClosable ? onCancel : undefined}
      />
      <div className="fixed inset-0 z-[61] flex items-center justify-center px-4">
        <div className="w-full max-w-[302px] rounded-[20px] bg-white shadow-[0_24px_48px_-12px_rgba(16,24,40,0.4)] py-5">
          <div className="px-6">
            <div
              className={`mx-auto mb-3 inline-flex h-[44px] w-[44px] items-center justify-center rounded-[22px] ${
                iconTone === "danger" ? "bg-error-50 text-error-600" : "bg-brand-100 text-brand-900"
              }`}
            >
              {icon}
            </div>
            <h3 className="text-center text-[15px] leading-[19.5px] font-bold text-[#181d27]">{title}</h3>
            <div className="mt-2 text-center text-[12px] leading-[18.6px] text-[#181d27]">{description}</div>
          </div>

          <div className="mt-5 flex items-center justify-center gap-1.5 px-5">
            <Button
              type="button"
              variant="ghost"
              className="h-10 min-w-[103px] rounded-[8px] px-3.5 text-[14px] font-semibold text-brand-800"
              onClick={onCancel}
              disabled={loading}
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              className={`h-10 min-w-[103px] rounded-[8px] px-3.5 text-[14px] font-semibold text-white ${
                confirmTone === "danger" ? "bg-error-600 hover:bg-error-700" : "bg-brand-900 hover:bg-brand-800"
              }`}
              onClick={onConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {loadingLabel || confirmLabel}
                </>
              ) : (
                confirmLabel
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ActionPopup;
